import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useLoading } from '../hooks/useLoading';
import { useToast } from '../context/ToastContext';
import { Topbar } from '../components/Topbar';
import { 
  FileText, Calendar, PlusCircle, Trash2, Edit3, 
  Users, BookOpen, Clock, CheckCircle2, AlertCircle,
  XCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, Button, Badge, Skeleton, Input } from '../components/ui';

export function ExamsPage() {
  const { user } = useAuth();
  const data = useData();
  const { addToast } = useToast();
  const loading = useLoading(500);

  const isProfessor = user?.type === 'professor';
  const { activities, setActivities, allEnrollments, courses } = data;

  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    course: '',
    turma: 'A',
    date: '',
    type: 'Prova'
  });

  const profCourses = user?.courses ? user.courses.split(',').map(c => c.trim().toLowerCase()) : [];

  const filteredExams = (activities || []).filter(ex => {
    if (isProfessor) {
      const courseMatch = profCourses.length === 0 || profCourses.includes(ex.course.toLowerCase());
      return courseMatch;
    } else {
      // Aluno vê apenas do seu curso e turma
      const enrollment = (allEnrollments || []).find(e => e.enrollmentId === user?.enrollmentId);
      if (!enrollment) return false;
      return ex.course === enrollment.course && ex.turma === enrollment.turma;
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const newExam = {
      ...formData,
      id: Date.now(),
      status: 'Agendada'
    };
    setActivities(prev => [...(prev || []), newExam]);
    data.addAuditLog(`Nova avaliação criada: ${formData.title} para ${formData.course} (${formData.turma})`, user);
    addToast('Avaliação agendada com sucesso!', 'success');
    setShowAdd(false);
    setFormData({ title: '', course: '', turma: 'A', date: '', type: 'Prova' });
  };

  const handleDelete = (id) => {
    if (window.confirm('Deseja realmente cancelar esta avaliação?')) {
      setActivities(prev => prev.filter(ex => ex.id !== id));
      addToast('Avaliação cancelada.', 'info');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Topbar />
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
        <div>
          <h2 className="text-4xl font-black tracking-tighter uppercase">Gestão de Avaliações</h2>
          <p className="text-muted font-medium text-lg">Acompanhamento e agendamento de atividades acadêmicas.</p>
        </div>
        {isProfessor && (
          <Button className="flex items-center gap-2 py-4 px-8 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-purple/20" onClick={() => setShowAdd(true)}>
            <PlusCircle size={20} /> Agendar Avaliação
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <Card title="Próximas Atividades" className="rounded-[2.5rem] p-8">
            <div className="space-y-6 mt-4">
              {loading ? (
                [1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)
              ) : filteredExams.length > 0 ? (
                filteredExams.map(ex => (
                  <div key={ex.id} className="group relative flex flex-col md:flex-row md:items-center justify-between gap-6 p-6 bg-white/5 border border-white/10 rounded-3xl hover:border-purple/40 transition-all duration-300">
                    <div className="flex items-center gap-5">
                      <div className="w-14 h-14 bg-purple/10 rounded-2xl flex items-center justify-center text-purple group-hover:bg-purple group-hover:text-white transition-all duration-500 shadow-inner">
                        <FileText size={28} />
                      </div>
                      <div>
                        <h4 className="text-xl font-black mb-1">{ex.title}</h4>
                        <div className="flex flex-wrap items-center gap-3">
                          <Badge variant="blue" className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5">{ex.course}</Badge>
                          <Badge variant="purple" className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5">Turma {ex.turma}</Badge>
                          <span className="text-xs text-muted font-bold flex items-center gap-1.5">
                            <Clock size={12} className="text-purple" />
                            {ex.type}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-8">
                      <div className="text-right">
                        <span className="block text-[9px] text-muted font-black uppercase tracking-widest mb-1">Data Prevista</span>
                        <strong className="text-lg font-mono text-white">{new Date(ex.date).toLocaleDateString('pt-BR')}</strong>
                      </div>
                      {isProfessor && (
                        <button 
                          onClick={() => handleDelete(ex.id)}
                          className="p-3 bg-red/5 text-red rounded-xl hover:bg-red hover:text-white transition-all duration-300 border border-red/10"
                        >
                          <Trash2 size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-20 bg-white/5 rounded-[2.5rem] border-2 border-dashed border-white/5">
                  <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 opacity-20">
                    <Calendar size={48} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Sem avaliações agendadas</h3>
                  <p className="text-muted max-w-xs mx-auto text-sm">Não existem atividades avaliativas registradas para este período.</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-4 space-y-8">
          <Card title="Resumo Acadêmico" className="rounded-[2.5rem] p-8">
            <div className="space-y-6 mt-4">
              <div className="flex items-center justify-between p-4 bg-purple/5 rounded-2xl border border-purple/10">
                <div className="flex flex-col">
                  <span className="text-[10px] text-purple font-black uppercase tracking-widest">Total Agendado</span>
                  <strong className="text-2xl text-white font-black">{filteredExams.length}</strong>
                </div>
                <div className="w-10 h-10 bg-purple/20 rounded-xl flex items-center justify-center text-purple">
                  <BookOpen size={20} />
                </div>
              </div>

              <div className="p-5 bg-blue/5 rounded-3xl border border-blue/10">
                <h5 className="text-xs font-black uppercase tracking-widest text-blue mb-4 flex items-center gap-2">
                  <AlertCircle size={14} />
                  Informações Importantes
                </h5>
                <ul className="space-y-3">
                  {[
                    'Presença obrigatória em provas',
                    'Atraso máximo permitido: 15min',
                    'Documento com foto necessário',
                    'Uso de calculadora sob consulta'
                  ].map((text, i) => (
                    <li key={i} className="flex items-center gap-2 text-[11px] font-bold text-muted">
                      <div className="w-1 h-1 rounded-full bg-blue" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Modal Agendamento */}
      <AnimatePresence>
        {showAdd && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-lg bg-[#0b1322] border border-white/10 rounded-[3rem] p-10 shadow-2xl"
            >
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h3 className="text-3xl font-black uppercase tracking-tighter">Agendar Avaliação</h3>
                  <p className="text-sm text-muted font-medium">Defina os parâmetros para a nova atividade.</p>
                </div>
                <button onClick={() => setShowAdd(false)} className="text-muted hover:text-white transition-colors">
                  <XCircle size={32}/>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="formGroup">
                  <label className="text-[10px] uppercase font-black tracking-widest text-muted mb-2 block ml-1">Título da Atividade</label>
                  <input 
                    required 
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold"
                    placeholder="Ex: Prova Mensal de Anatomia"
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="formGroup">
                    <label className="text-[10px] uppercase font-black tracking-widest text-muted mb-2 block ml-1">Curso</label>
                    <select 
                      required
                      className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold text-sm"
                      value={formData.course}
                      onChange={e => setFormData({...formData, course: e.target.value})}
                    >
                      <option value="">Selecionar...</option>
                      {profCourses.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                      {profCourses.length === 0 && (courses || []).filter(c => c.status === 'Ativo').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="formGroup">
                    <label className="text-[10px] uppercase font-black tracking-widest text-muted mb-2 block ml-1">Turma</label>
                    <select 
                      className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold text-sm"
                      value={formData.turma}
                      onChange={e => setFormData({...formData, turma: e.target.value})}
                    >
                      <option value="A">Turma A</option>
                      <option value="B">Turma B</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="formGroup">
                    <label className="text-[10px] uppercase font-black tracking-widest text-muted mb-2 block ml-1">Tipo</label>
                    <select 
                      className="w-full bg-[#070d19] border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold text-sm"
                      value={formData.type}
                      onChange={e => setFormData({...formData, type: e.target.value})}
                    >
                      <option>Prova</option>
                      <option>Trabalho</option>
                      <option>Seminário</option>
                      <option>Atividade Prática</option>
                    </select>
                  </div>
                  <div className="formGroup">
                    <label className="text-[10px] uppercase font-black tracking-widest text-muted mb-2 block ml-1">Data de Aplicação</label>
                    <input 
                      type="date" 
                      required 
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white outline-none focus:border-purple font-bold text-sm"
                      value={formData.date}
                      onChange={e => setFormData({...formData, date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="flex gap-4 pt-6">
                  <Button variant="outline" className="flex-1 py-4 rounded-2xl font-black uppercase tracking-widest" type="button" onClick={() => setShowAdd(false)}>CANCELAR</Button>
                  <Button className="flex-1 py-4 rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-purple/20" type="submit">AGENDAR AGORA</Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}